/**
 * ReturnToOrderBar — the "take them back to what they were doing" half
 * of the top-up loop (launch-gap 09, Hunter). Order surfaces link to
 * the billing pages with `?returnTo=<in-app path>`; when the param is
 * present (and passes the `safeReturnTo` allow-list) the billing page
 * renders this bar with a single CTA back to the order.
 *
 * The family page flips `landed` once `use-top-up-watch` confirms the
 * Stripe credit arrived, upgrading the copy to "funds added" and the
 * button to the primary variant — the parent's next tap finishes their
 * order.
 */

import { Button } from '@ordo/ui/button'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router'

export interface ReturnToOrderBarProps {
  /** Pre-validated in-app path (run through `safeReturnTo` by the caller). */
  returnTo: string
  /** True once the watched top-up landed on the balance. */
  landed?: boolean
}

export function ReturnToOrderBar({ returnTo, landed = false }: ReturnToOrderBarProps): ReactElement {
  const navigate = useNavigate()
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border bg-card p-4">
      <div className="flex min-w-0 items-center gap-2.5">
        {landed ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
        ) : (
          <ArrowLeft className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <p className="min-w-0 text-sm text-foreground">
          {landed
            ? 'Funds added — your balance is updated. Pick up your order where you left off.'
            : 'Adding funds for an order? Head back whenever you’re ready.'}
        </p>
      </div>
      <Button
        variant={landed ? 'default' : 'outline'}
        size="sm"
        className="shrink-0"
        onClick={() => void navigate(returnTo)}>
        Back to your order
      </Button>
    </div>
  )
}
